import { inject, Injectable } from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { Store } from '@ngrx/store';
import { map, Observable, of } from 'rxjs';
import { DateTime } from 'luxon';
import { PatientDetails, ProgressNoteStatus, Provider } from '@ffd/api-client';
import { AddNewTreatmentData, AddTreatmentModalService } from '@libs/charting';
import { AppointmentReason, patientActions, selectUserProvider } from '@libs/store';
import { isNotNullOrUndefined, routes } from '@libs/utils';
import { AddTreatmentItem, mapTreatmentToTreatmentElement, Tooth } from '@libs/ui-lib';
import {
  CompleteAppointmentModalComponent,
  CompleteAppointmentModalComponentData,
  CompleteAppointmentModalComponentResult,
} from '../create-appointment/complete-appointment-modal/complete-appointment-modal.component';

export interface CompleteAppointmentParams {
  patient: PatientDetails;
  appointmentDate: DateTime;
  procedures: AppointmentReason[];
  teeth: Tooth[];
  reopen?: boolean;
}

export interface CompleteTreatmentsParams {
  treatments: AddTreatmentItem[];
  patientId: string;
  siteId: string;
  provider?: Provider;
}

@Injectable()
export class CompleteAppointmentService {
  private readonly store = inject(Store);
  private readonly dialog = inject(Dialog);
  private readonly addTreatmentModalService = inject(AddTreatmentModalService);

  /**
   * Opens the complete appointment modal with the provided data
   * @returns Observable that emits the completed treatments array or empty if cancelled/skipped
   */
  public openCompleteAppointmentModal$(params: CompleteAppointmentParams): Observable<AddTreatmentItem[] | undefined> {
    // Map procedures to treatments
    const treatments = this.mapProceduresToTreatments(params.procedures);

    if (treatments.length === 0) {
      // No treatments found for procedures - return empty array to still allow status update
      return of([]);
    }

    return this.dialog
      .open<CompleteAppointmentModalComponentResult, CompleteAppointmentModalComponentData, CompleteAppointmentModalComponent>(
        CompleteAppointmentModalComponent,
        {
          backdropClass: ['bg-overlay-light'],
          data: {
            selectedPatient: params.patient,
            appointmentDate: params.appointmentDate.toString(),
            treatments,
            teeth: params.teeth,
            reopen: params.reopen,
          },
          disableClose: true,
        },
      )
      .closed.pipe(
        map((result) => {
          if (!result) {
            // User cancelled
            return;
          }
          // Return empty array for skip or the actual treatments
          return result.completedTreatments;
        }),
      );
  }

  /**
   * Completes treatments by dispatching the onSetCompletedTreatments action
   */
  public completeTreatments(params: CompleteTreatmentsParams): void {
    const newTreatments: AddNewTreatmentData[] = params.treatments.map((treatment) => ({
      ...treatment,
      status: ProgressNoteStatus.C,
    }));

    this.store.dispatch(
      patientActions.onSetCompletedTreatments({
        newTreatments,
        isShowcase: params.siteId === routes.showcaseSiteId,
        provider: params.provider ?? this.store.selectSignal(selectUserProvider)(),
        patientId: params.patientId,
      }),
    );
  }

  /**
   * Maps appointment procedures to treatment data objects
   */
  private mapProceduresToTreatments(procedures: AppointmentReason[]): AddNewTreatmentData[] {
    const allTreatments = this.addTreatmentModalService.selectSignal((state) => state)().treatments.data ?? [];

    return procedures
      .map((procedure) => {
        const treatment = allTreatments.find((t) => t.dentalCode === procedure.code);
        return treatment
          ? {
              ...mapTreatmentToTreatmentElement(treatment),
              status: ProgressNoteStatus.C,
            }
          : undefined;
      })
      .filter((treatment): treatment is AddNewTreatmentData => isNotNullOrUndefined(treatment));
  }
}
